// Verwaltung der Vorgangsarten (#489, Etappe V1) — läuft im Formularbereich der Pille
// (dunkles Theme, Muster: ChecklistenVorlagenVerwaltung). Lesen: ganzes Team; ändern: nur Admins.
import React, { useEffect, useMemo, useState } from 'react';
import { ArrowLeft, Info, Loader2, Lock, Plus, Workflow, X } from 'lucide-react';
import { supabase } from '../../../lib/supabase/client';
import { FormLoeschKnopf } from '../../../components/island/form/FormLoeschKnopf';
import {
  FREIGABE_STUFEN, darfVorgaengePflegen, entwurfAus, ladeVorgangsarten, leererEntwurf, pruefeEntwurf,
  type Pflichtangabe, type Vorgangsart, type VorgangsartEntwurf,
} from '../../../lib/vorgaenge/vorgangsarten';
import { FUNKTIONEN, ladeSchalter, speichereSchalter, type FunktionKey, type Schalter } from '../../../lib/vorgaenge/funktionen';

const LABEL = 'text-[9px] font-black text-white/40 uppercase tracking-widest mb-1.5 block';
const INPUT = 'w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-sm font-bold text-white placeholder:text-white/30 focus:outline-none focus:ring-2 focus:ring-[#c4a77d]/40 focus:border-[#c4a77d]/40 disabled:opacity-60';
const SELECT = `${INPUT} [&>option]:bg-[#1c1c1e]`;

interface Auswahl { id: string; name: string; parent_id?: string | null }

interface Stammdaten {
  kategorien: Auswahl[];
  checklisten: Auswahl[];
  mailvorlagen: Auswahl[];
  mitarbeiter: Auswahl[];
  teams: Auswahl[];
}

export default function VorgangsVorlagenVerwaltung() {
  const [arten, setArten] = useState<Vorgangsart[]>([]);
  const [stamm, setStamm] = useState<Stammdaten>({ kategorien: [], checklisten: [], mailvorlagen: [], mitarbeiter: [], teams: [] });
  const [darf, setDarf] = useState(false);
  const [laedt, setLaedt] = useState(true);
  const [ladeFehler, setLadeFehler] = useState<string | null>(null);
  const [entwurf, setEntwurf] = useState<VorgangsartEntwurf | null>(null);
  const [speichert, setSpeichert] = useState(false);
  const [fehler, setFehler] = useState<string[]>([]);
  const [schalter, setSchalter] = useState<Schalter>({ erkennung: false, startkarte: false, nachfrage: false });
  const [schaltetGerade, setSchaltetGerade] = useState<FunktionKey | null>(null);
  const [schalterFehler, setSchalterFehler] = useState<string | null>(null);

  const lade = async () => {
    try {
      const [a, k, c, m, p, t, d, s] = await Promise.all([
        ladeVorgangsarten(),
        supabase.from('ticket_kategorien').select('id, name, parent_id').eq('aktiv', true).order('sortierung'),
        supabase.from('ticket_checklisten_vorlagen').select('id, name').eq('aktiv', true).order('sortierung').order('name'),
        supabase.from('email_vorlagen').select('id, name').eq('aktiv', true).order('name'),
        supabase.from('profiles').select('id, full_name').or('role.eq.team,role.eq.admin').eq('zuweisbar', true).order('full_name'),
        supabase.from('teams').select('id, name').order('name'),
        darfVorgaengePflegen(),
        ladeSchalter(),
      ]);
      setArten(a);
      setSchalter(s);
      setStamm({
        kategorien: (k.data ?? []) as Auswahl[],
        checklisten: (c.data ?? []) as Auswahl[],
        mailvorlagen: (m.data ?? []) as Auswahl[],
        mitarbeiter: ((p.data ?? []) as { id: string; full_name: string | null }[]).filter(x => x.full_name).map(x => ({ id: x.id, name: x.full_name ?? '' })),
        teams: (t.data ?? []) as Auswahl[],
      });
      setDarf(d);
      setLadeFehler(null);
    } catch (err) {
      setLadeFehler(err instanceof Error ? err.message : 'Laden fehlgeschlagen');
    } finally {
      setLaedt(false);
    }
  };
  useEffect(() => { void lade(); }, []);

  const schalte = async (key: FunktionKey) => {
    if (!darf || schaltetGerade) return;
    const neu = { ...schalter, [key]: !schalter[key] };
    setSchaltetGerade(key);
    setSchalterFehler(null);
    try { await speichereSchalter(neu); setSchalter(neu); }
    catch (err) { setSchalterFehler(err instanceof Error ? err.message : 'Speichern fehlgeschlagen'); }
    finally { setSchaltetGerade(null); }
  };

  const name = (liste: Auswahl[], id: string | null) => (id ? liste.find(x => x.id === id)?.name ?? '–' : '–');
  const hauptKategorien = useMemo(() => stamm.kategorien.filter(k => !k.parent_id), [stamm.kategorien]);

  const speichern = async () => {
    if (!entwurf || speichert) return;
    const { fehler: f, zeile } = pruefeEntwurf(entwurf, arten.filter(a => a.id !== entwurf.id).map(a => a.name));
    if (!zeile) { setFehler(f); return; }
    setSpeichert(true);
    setFehler([]);
    const res = entwurf.id
      ? await supabase.from('vorgangsarten').update(zeile).eq('id', entwurf.id)
      : await supabase.from('vorgangsarten').insert(zeile);
    setSpeichert(false);
    if (res.error) { setFehler([res.error.message]); return; }
    setEntwurf(null);
    await lade();
  };

  const loeschen = async () => {
    if (!entwurf?.id || speichert) return;
    // Rückfrage (früher window.confirm) steckt im Zwei-Klick-Knopf unten, Text unverändert.
    setSpeichert(true);
    const { error } = await supabase.from('vorgangsarten').delete().eq('id', entwurf.id);
    setSpeichert(false);
    if (error) { setFehler([error.message]); return; }
    setEntwurf(null);
    await lade();
  };

  if (laedt) {
    return <div className="flex items-center justify-center py-10"><Loader2 size={18} className="animate-spin text-white/30" /></div>;
  }
  if (ladeFehler) {
    return <p className="p-4 text-[11px] font-bold text-red-400">Vorgangsarten konnten nicht geladen werden: {ladeFehler}</p>;
  }

  // ── Editor ────────────────────────────────────────────────────────────────
  if (entwurf) {
    const e = entwurf;
    const set = <K extends keyof VorgangsartEntwurf>(k: K, v: VorgangsartEntwurf[K]) => setEntwurf({ ...e, [k]: v });
    const setAngabe = (i: number, a: Partial<Pflichtangabe>) => set('pflichtangaben', e.pflichtangaben.map((x, j) => (j === i ? { ...x, ...a } : x)));
    const unterKategorien = stamm.kategorien.filter(k => k.parent_id && k.parent_id === e.kategorie_id);
    const gesperrt = !darf;

    return (
      <div className="p-4 flex flex-col gap-4">
        <button type="button" onClick={() => { setEntwurf(null); setFehler([]); }}
          className="flex items-center gap-1.5 self-start text-[10px] font-black uppercase tracking-widest text-white/40 hover:text-white transition-colors">
          <ArrowLeft size={12} /> Zur Liste
        </button>
        {gesperrt && (
          <p className="flex items-center gap-1.5 text-[11px] font-bold text-white/50"><Lock size={12} /> Nur Admins können Vorgangsarten ändern.</p>
        )}
        <fieldset disabled={gesperrt} className="flex flex-col gap-4 min-w-0">
          <div>
            <label className={LABEL}>Name *</label>
            <input type="text" value={e.name} onChange={x => set('name', x.target.value)} placeholder="z. B. Schlüssel nachbestellen" className={INPUT} />
          </div>
          <div>
            <label className={LABEL}>Erklärung für das Team (was passiert, was nicht)</label>
            <textarea value={e.erklaerung} onChange={x => set('erklaerung', x.target.value)} rows={3}
              placeholder="Erkennt … Fehlende Angaben werden erfragt … Freigabe durch …" className={`${INPUT} resize-none leading-relaxed`} />
          </div>
          <div>
            <label className={LABEL}>Woran erkennt die KI diesen Vorgang?</label>
            <textarea value={e.beschreibung_ki} onChange={x => set('beschreibung_ki', x.target.value)} rows={3}
              placeholder="Ein Mieter meldet … / fragt nach …" className={`${INPUT} resize-none leading-relaxed`} />
          </div>
          <div>
            <label className={LABEL}>Typische Wörter (Hinweis für die KI, durch Komma getrennt)</label>
            <input type="text" value={e.stichwoerter} onChange={x => set('stichwoerter', x.target.value)} placeholder="kündigung, nachmieter, auszug" className={INPUT} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={LABEL}>Ticket-Kategorie</label>
              <select value={e.kategorie_id} onChange={x => setEntwurf({ ...e, kategorie_id: x.target.value, unterkategorie_id: '' })} className={SELECT}>
                <option value="">–</option>
                {hauptKategorien.map(k => <option key={k.id} value={k.id}>{k.name}</option>)}
              </select>
            </div>
            <div>
              <label className={LABEL}>Unterkategorie</label>
              <select value={e.unterkategorie_id} onChange={x => set('unterkategorie_id', x.target.value)} disabled={gesperrt || unterKategorien.length === 0} className={SELECT}>
                <option value="">–</option>
                {unterKategorien.map(k => <option key={k.id} value={k.id}>{k.name}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className={LABEL}>Checkliste</label>
            <select value={e.checkliste_vorlage_id} onChange={x => set('checkliste_vorlage_id', x.target.value)} className={SELECT}>
              <option value="">Keine</option>
              {stamm.checklisten.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div>
            <label className={LABEL}>Antwortvorlage (erste Antwort an den Absender)</label>
            <select value={e.antwort_vorlage_id} onChange={x => set('antwort_vorlage_id', x.target.value)} className={SELECT}>
              <option value="">Keine – KI schreibt frei nach den Regeln</option>
              {stamm.mailvorlagen.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
            </select>
          </div>

          <div>
            <label className={LABEL}>Pflichtangaben</label>
            <div className="flex flex-col gap-1.5">
              {e.pflichtangaben.length === 0 && <p className="text-[11px] text-white/30 italic">Noch keine Angaben.</p>}
              {e.pflichtangaben.map((a, i) => (
                <div key={i} className="flex items-center gap-1.5">
                  <input type="text" value={a.bezeichnung} onChange={x => setAngabe(i, { bezeichnung: x.target.value, schluessel: '' })}
                    placeholder="z. B. Fotos" aria-label={`Angabe ${i + 1}`} className={`${INPUT} py-2`} />
                  <select value={a.quelle} onChange={x => setAngabe(i, { quelle: x.target.value === 'app' ? 'app' : 'absender' })}
                    aria-label={`Herkunft Angabe ${i + 1}`} className={`${SELECT} py-2 w-[150px] shrink-0`}>
                    <option value="absender">beim Absender erfragen</option>
                    <option value="app">kennt die App</option>
                  </select>
                  <button type="button" onClick={() => set('pflichtangaben', e.pflichtangaben.filter((_, j) => j !== i))} aria-label={`Angabe ${i + 1} entfernen`}
                    className="p-2 rounded-lg text-white/40 hover:text-red-400 shrink-0"><X size={14} /></button>
                </div>
              ))}
              <button type="button" onClick={() => set('pflichtangaben', [...e.pflichtangaben, { schluessel: '', bezeichnung: '', quelle: 'absender' }])}
                className="flex items-center gap-1.5 self-start text-[10px] font-black uppercase tracking-widest text-[#c4a77d] hover:text-white">
                <Plus size={12} /> Angabe
              </button>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={LABEL}>Frist (Tage nach Eingang)</label>
              <input type="text" inputMode="numeric" value={e.faellig_tage} onChange={x => set('faellig_tage', x.target.value)} placeholder="leer = keine" className={INPUT} />
            </div>
            <div>
              <label className={LABEL}>Wiedervorlage (Tage)</label>
              <input type="text" inputMode="numeric" value={e.wiedervorlage_tage} onChange={x => set('wiedervorlage_tage', x.target.value)} placeholder="leer = keine" className={INPUT} />
            </div>
          </div>
          <div>
            <label className={LABEL}>Zuständig</label>
            <select value={e.zustaendig} onChange={x => set('zustaendig', x.target.value)} className={SELECT}>
              <option value="">Wie bisher (Vorschlag der Analyse)</option>
              {stamm.teams.length > 0 && <optgroup label="Teams">{stamm.teams.map(t => <option key={t.id} value={`t:${t.id}`}>{t.name}</option>)}</optgroup>}
              <optgroup label="Mitarbeiter">{stamm.mitarbeiter.map(m => <option key={m.id} value={`m:${m.id}`}>{m.name}</option>)}</optgroup>
            </select>
          </div>
          <div>
            <label className={LABEL}>Freigabe</label>
            <select value={e.freigabe_stufe} onChange={x => set('freigabe_stufe', x.target.value === 'vorschlag' ? 'vorschlag' : 'freigabe')} className={SELECT}>
              {FREIGABE_STUFEN.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
            </select>
            <p className="mt-1.5 text-[10px] text-white/35 leading-relaxed">
              {FREIGABE_STUFEN.find(s => s.key === e.freigabe_stufe)?.hinweis} Automatisch versendet wird nichts.
            </p>
          </div>
          <label className="flex items-center gap-2 text-[11px] font-bold text-white/60 cursor-pointer">
            <input type="checkbox" checked={e.rechtlich_sensibel} onChange={x => set('rechtlich_sensibel', x.target.checked)} className="accent-[#c4a77d]" />
            Rechtlich sensibel (Freigabe immer durch einen Admin)
          </label>
          <label className="flex items-center gap-2 text-[11px] font-bold text-white/60 cursor-pointer">
            <input type="checkbox" checked={e.aktiv} onChange={x => set('aktiv', x.target.checked)} className="accent-[#c4a77d]" />
            Aktiv (wird bei neuen Mails erkannt)
          </label>
        </fieldset>
        {fehler.length > 0 && <div className="flex flex-col gap-0.5">{fehler.map(f => <p key={f} className="text-[11px] font-bold text-red-400">{f}</p>)}</div>}
        {darf && (
          <div className="flex items-center gap-2">
            <button type="button" onClick={() => { void speichern(); }} disabled={speichert}
              className="flex-1 flex items-center justify-center gap-1.5 rounded-xl bg-[#c4a77d] px-4 py-2.5 text-[11px] font-black uppercase tracking-widest text-white transition-colors hover:bg-[#b3966c] disabled:opacity-50">
              {speichert ? <Loader2 size={13} className="animate-spin" /> : null} Speichern
            </button>
            {e.id && (
              <FormLoeschKnopf frage={`Vorgangsart „${e.name}" wirklich löschen? Bestehende Tickets behalten ihre Daten, verlieren aber die Zuordnung.`}
                tip="Vorgangsart löschen" gesperrt={speichert} onLoeschen={() => { void loeschen(); }} />
            )}
          </div>
        )}
      </div>
    );
  }

  // ── Liste ─────────────────────────────────────────────────────────────────
  return (
    <div className="p-4 flex flex-col gap-3">
      <p className="text-[11px] font-medium text-white/40 leading-relaxed">
        Eine Vorgangsart bündelt Kategorie, Checkliste, Antwortvorlage, Pflichtangaben und Fristen.
        Die KI erkennt sie an neuen Mails und schlägt den Vorgang vor; versendet wird nur nach Freigabe.
      </p>

      <div className="flex flex-col gap-1.5">
        <span className={LABEL}>Funktionen</span>
        {FUNKTIONEN.map(f => {
          const an = f.verfuegbar && schalter[f.key];
          return (
            <div key={f.key} className="rounded-xl bg-white/5 border border-white/10 px-3 py-2.5 flex flex-col gap-1">
              <div className="flex items-center gap-2.5">
                <span className="min-w-0 flex-1 text-[12px] font-bold text-white">{f.titel}</span>
                {f.verfuegbar ? (
                  <button type="button" role="switch" aria-checked={an} aria-label={f.titel} disabled={!darf || schaltetGerade !== null}
                    onClick={() => { void schalte(f.key); }}
                    className={`relative h-6 w-10 shrink-0 rounded-full transition-colors disabled:opacity-50 ${an ? 'bg-emerald-500' : 'bg-white/15'}`}>
                    <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-all ${an ? 'left-[18px]' : 'left-0.5'}`} />
                  </button>
                ) : (
                  <span className="shrink-0 rounded-full bg-white/10 px-2 py-0.5 text-[9px] font-black uppercase tracking-widest text-white/40">kommt ({f.etappe})</span>
                )}
              </div>
              <p className="text-[10px] text-white/45 leading-relaxed">{f.erklaerung}</p>
              <p className="flex items-start gap-1 text-[10px] text-white/35 leading-relaxed"><Info size={11} className="mt-px shrink-0" />{f.nicht}</p>
            </div>
          );
        })}
        {schalterFehler && <p className="text-[11px] font-bold text-red-400">{schalterFehler}</p>}
      </div>

      <span className={LABEL}>Vorgangsarten</span>
      <div className="flex flex-col gap-1.5">
        {arten.length === 0 && <p className="text-[11px] font-medium text-white/30 italic py-2">Noch keine Vorgangsarten.</p>}
        {arten.map(a => (
          <button key={a.id} type="button" onClick={() => { setFehler([]); setEntwurf(entwurfAus(a)); }}
            className="flex items-center gap-2.5 rounded-xl bg-white/5 border border-white/10 px-3 py-2.5 text-left hover:border-[#c4a77d]/50 transition-colors">
            <Workflow size={14} className="text-[#c4a77d] shrink-0" />
            <span className="min-w-0 flex-1">
              <span className="block truncate text-[12px] font-bold text-white">{a.name}</span>
              {a.erklaerung && <span className="block text-[10px] text-white/50 leading-snug line-clamp-2">{a.erklaerung}</span>}
              <span className="block truncate text-[10px] text-white/40">
                {name(stamm.kategorien, a.kategorie_id)} · {a.pflichtangaben.length} Angaben
                {a.faellig_tage != null ? ` · Frist ${a.faellig_tage} T.` : ''}{a.aktiv ? '' : ' · inaktiv'}
              </span>
            </span>
          </button>
        ))}
      </div>
      {darf ? (
        <button type="button" onClick={() => { setFehler([]); setEntwurf(leererEntwurf((arten.at(-1)?.sortierung ?? 0) + 10)); }}
          className="flex items-center justify-center gap-1.5 w-full rounded-xl bg-[#c4a77d] px-4 py-2.5 text-[11px] font-black uppercase tracking-widest text-white transition-colors hover:bg-[#b3966c]">
          <Plus size={14} /> Neue Vorgangsart
        </button>
      ) : (
        <p className="flex items-center gap-1.5 text-[10px] font-bold text-white/35"><Lock size={11} /> Ändern nur durch Admins.</p>
      )}
    </div>
  );
}
